import {
  AfterViewInit,
  Component,
  ElementRef,
  NgZone,
  OnDestroy,
  OnInit,
  ViewChild,
  inject,
} from '@angular/core';
import mapboxgl from 'mapbox-gl';
import {
  Subscription,
  debounceTime,
  distinctUntilChanged,
  filter,
  fromEvent,
  map,
} from 'rxjs';
import * as turf from '@turf/turf';
import { Router } from '@angular/router';
import {
  IActivo,
  IFilter,
  IQueryParam,
  IRecorridoDinamico,
  IUbicacion,
  IUpdateActivo,
} from 'modelos/src';
import { DireccionesService } from '../../../../../auxiliares/servicios/http/direcciones.service';
import { RecorridosService } from '../../../../../auxiliares/servicios/http/recorridos.service';
import { RecorridosDinamicosService } from '../../../../../auxiliares/servicios/http/recorridos-dinamicos.service';
import { ActivosService } from '../../../../../auxiliares/servicios/http/activos.service';
import { HelperService } from '../../../../../auxiliares/servicios/helper.service';
import { Lista2Service } from '../../../../../auxiliares/servicios/lista2.service';
import { ParamsService } from '../../../../../auxiliares/servicios/params.service';
import { DateRangeValue } from '../../../../../componentes/date-picker/date-picker.component';
import { environment } from '../../../../../../environments/environment';
import { aplicarEtiquetasMapa } from '../../../../../auxiliares/mapbox/etiquetas-mapa';

interface PuntoRuta {
  coordenadas: [number, number];
  direccion?: string;
  marker: mapboxgl.Marker;
}

const RUTA_SOURCE_ID = 'planificador-ruta-linea';
const TOLERANCIA_SOURCE_ID = 'planificador-ruta-tolerancia';

@Component({
  selector: 'app-planificador-ruta-vehiculos',
  templateUrl: './planificador-ruta.component.html',
  styleUrl: './planificador-ruta.component.scss',
  standalone: false,
})
export class PlanificadorRutaVehiculosComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  private ngZone = inject(NgZone);
  private direccionesService = inject(DireccionesService);
  private recorridosService = inject(RecorridosService);
  private recorridosDinamicosService = inject(RecorridosDinamicosService);
  private activosService = inject(ActivosService);
  private lista2 = inject(Lista2Service);
  private paramsService = inject(ParamsService);
  private navigator = inject(Router);
  helper = inject(HelperService);

  @ViewChild('mapContainer', { static: false })
  mapContainer?: ElementRef<HTMLElement>;
  @ViewChild('inputDireccion') inputDireccion?: ElementRef;

  private map?: mapboxgl.Map;

  public puntos: PuntoRuta[] = [];

  // Búsqueda de direcciones
  public direccionBuscada = '';
  public predicciones?: string[];
  public showSuggestions = false;
  public buscandoDireccion = false;

  // Inicio/fin fijo
  public definirInicioFin = false;
  public puntoInicio?: PuntoRuta;
  public puntoFin?: PuntoRuta;

  // Cálculo
  public calculando = false;
  public resultado?: {
    distanciaKm: number;
    duracionMin: number;
    geometry: [number, number][];
  };

  // Zonas ya declaradas (además de click en el mapa y búsqueda de dirección)
  public zonas: IUbicacion[] = [];
  public zonas$?: Subscription;
  public zonaSeleccionada?: IUbicacion;

  // Guardar y asignar a un vehículo
  public vehiculos: IActivo[] = [];
  public vehiculos$?: Subscription;
  public vehiculoSeleccionado?: IActivo;
  public nombreRecorrido = '';
  public ventana?: DateRangeValue;
  public radioToleranciaMetros = 60;
  public guardando = false;

  // Edición de un recorrido existente (viene por ParamsService desde el listado)
  public modoEdicion = false;
  private recorridoAEditar?: IRecorridoDinamico;

  get puedeCalcular(): boolean {
    if (this.calculando || this.puntos.length < 2) return false;
    if (!this.definirInicioFin) return true;
    return (
      !!this.puntoInicio &&
      !!this.puntoFin &&
      this.puntoInicio !== this.puntoFin
    );
  }

  get puedeGuardar(): boolean {
    return (
      !this.guardando &&
      !!this.resultado &&
      !!this.vehiculoSeleccionado &&
      !!this.ventana?.start &&
      !!this.ventana?.end &&
      !!this.nombreRecorrido
    );
  }

  // ZONAS YA DECLARADAS

  public async listarZonas(): Promise<void> {
    const filter: IFilter<IUbicacion> = {
      categoria: { $in: ['Vehiculos', 'Zona'] },
    } as IFilter<IUbicacion>;
    const query: IQueryParam = {
      filter: JSON.stringify(filter),
      sort: 'identificacion',
      page: 0,
      includeChildren: true,
      childrenLevel: 0,
    };
    this.zonas$?.unsubscribe();
    this.zonas$ = this.lista2.ubicacions(query, 30).subscribe((data) => {
      this.zonas = data.datos as IUbicacion[];
    });
  }

  /** Coordenada representativa de una zona: directa si es Point/Circle, centroide si es Polygon. */
  private coordenadasDeZona(zona: IUbicacion): [number, number] | undefined {
    const geojson = zona.geojson;
    if (!geojson) return undefined;
    if (geojson.type === 'Polygon') {
      const centro = turf.centroid(geojson as turf.AllGeoJSON);
      return centro.geometry.coordinates as [number, number];
    }
    return geojson.coordinates as [number, number];
  }

  public agregarZona(): void {
    if (!this.zonaSeleccionada) return;
    const coordenadas = this.coordenadasDeZona(this.zonaSeleccionada);
    if (!coordenadas) {
      this.helper.notifError('La zona no tiene coordenadas');
      this.zonaSeleccionada = undefined;
      return;
    }
    this.agregarPunto(coordenadas, this.zonaSeleccionada.identificacion);
    this.map?.flyTo({ center: coordenadas, zoom: 15, duration: 800 });
    this.zonaSeleccionada = undefined;
  }

  // VEHÍCULOS

  public async listarVehiculos(): Promise<void> {
    const query: IQueryParam = {
      sort: 'nombre',
      page: 0,
      includeChildren: true,
      childrenLevel: 0,
    };
    this.vehiculos$?.unsubscribe();
    this.vehiculos$ = this.lista2.vehiculos(query, 30).subscribe((data) => {
      this.vehiculos = data.datos;
    });
  }

  // GUARDAR Y ASIGNAR

  public async guardarYAsignar(): Promise<void> {
    if (!this.puedeGuardar || !this.resultado || !this.vehiculoSeleccionado) {
      return;
    }

    this.guardando = true;
    try {
      const datos = {
        nombre: this.nombreRecorrido,
        geojson: {
          type: 'LineString' as const,
          coordinates: this.resultado.geometry,
        },
        paradas: this.puntos.map((p) => ({
          geojson: { type: 'Point' as const, coordinates: p.coordenadas },
          direccion: p.direccion,
          radioMetros: this.radioToleranciaMetros,
        })),
        idActivo: this.vehiculoSeleccionado._id,
        ventana: {
          fechaDesde: this.ventana!.start,
          fechaHasta: this.ventana!.end,
        },
      };

      const idVehiculoAnterior = this.recorridoAEditar?.idActivo;
      const recorrido = this.modoEdicion
        ? await this.recorridosDinamicosService.update(
            this.recorridoAEditar!._id!,
            datos,
          )
        : await this.recorridosDinamicosService.create(datos);

      // Si cambió de vehículo, liberar el puntero del anterior.
      if (
        idVehiculoAnterior &&
        idVehiculoAnterior !== this.vehiculoSeleccionado._id
      ) {
        await this.activosService.update(idVehiculoAnterior, {
          vehiculo: { idRecorridoDinamico: null },
        });
      }
      const updateActivo: IUpdateActivo = {
        vehiculo: { idRecorridoDinamico: recorrido._id },
      };
      await this.activosService.update(
        this.vehiculoSeleccionado._id!,
        updateActivo,
      );

      this.helper.notifSuccess(
        this.modoEdicion
          ? `Recorrido "${this.nombreRecorrido}" actualizado`
          : `Recorrido "${this.nombreRecorrido}" creado y asignado a ${this.vehiculoSeleccionado.identificacion}`,
      );
      this.navigator.navigate(['/vehiculos/recorridos-dinamicos']);
    } catch (error) {
      this.helper.notifError(error);
    }
    this.guardando = false;
  }

  // MAPA

  async ngOnInit(): Promise<void> {
    // getParams() es null si nunca se seteó (ej: navegación directa por URL)
    this.recorridoAEditar = this.paramsService.getParams()?.[
      'recorridoDinamico'
    ] as IRecorridoDinamico | undefined;

    // Puede llegar acá por un enlace/back del navegador a un recorrido que
    // se finalizó mientras tanto: el backend igual lo va a rechazar, pero
    // evitamos cargar el formulario para nada.
    const fechaHasta = this.recorridoAEditar?.ventana?.fechaHasta;
    if (fechaHasta && fechaHasta <= new Date().toISOString()) {
      this.helper.notifError('El recorrido ya terminó, no se puede editar.');
      this.navigator.navigate(['/vehiculos/recorridos-dinamicos']);
      return;
    }

    this.modoEdicion = !!this.recorridoAEditar;

    if (this.recorridoAEditar) {
      this.nombreRecorrido = this.recorridoAEditar.nombre ?? '';
      this.radioToleranciaMetros =
        this.recorridoAEditar.paradas?.[0]?.radioMetros ?? 60;
      if (this.recorridoAEditar.ventana?.fechaDesde) {
        this.ventana = {
          start: this.recorridoAEditar.ventana.fechaDesde,
          end: this.recorridoAEditar.ventana.fechaHasta ?? '',
        };
      }
    }

    await Promise.all([this.listarVehiculos(), this.listarZonas()]);

    if (this.recorridoAEditar?.idActivo) {
      this.vehiculoSeleccionado = this.vehiculos.find(
        (v) => v._id === this.recorridoAEditar!.idActivo,
      );
    }
  }

  ngAfterViewInit(): void {
    setTimeout(() => this.initMap(), 0);
  }

  ngOnDestroy(): void {
    this.map?.remove();
    this.vehiculos$?.unsubscribe();
    this.zonas$?.unsubscribe();
  }

  private initMap(): void {
    if (!this.mapContainer) return;

    // Crear el mapa fuera de la Angular Zone: el loop de render de Mapbox
    // dispararía change detection en cada frame si se crea adentro
    this.ngZone.runOutsideAngular(() => {
      this.map = new mapboxgl.Map({
        language: 'es', // Etiquetas del mapa base en español
        container: this.mapContainer!.nativeElement,
        style: 'mapbox://styles/mapbox/streets-v12',
        center: [-58.3816, -34.6037], // Buenos Aires
        zoom: 12,
        accessToken: environment.mapboxToken,
      });
      aplicarEtiquetasMapa(this.map);

      this.map.addControl(
        new mapboxgl.NavigationControl({
          showCompass: true,
          showZoom: true,
          visualizePitch: true,
        }),
        'bottom-right',
      );

      this.map.on('click', (e) => {
        const coordenadas: [number, number] = [e.lngLat.lng, e.lngLat.lat];
        // El listener corre fuera de la Angular Zone: sin ngZone.run el
        // cambio no dispara change detection
        this.ngZone.run(() => this.agregarPunto(coordenadas));
      });
    });

    this.listenDirChange();

    if (this.recorridoAEditar?.paradas?.length) {
      this.cargarPuntosDeRecorridoAEditar(this.recorridoAEditar.paradas);
    }
  }

  /**
   * Precarga los puntos del recorrido a editar como si el usuario los
   * hubiera agregado a mano, y centra el mapa para mostrarlos. No precarga
   * `resultado`: hay que recalcular la mejor ruta antes de poder guardar,
   * igual que al crear uno nuevo.
   */
  private cargarPuntosDeRecorridoAEditar(
    paradas: NonNullable<IRecorridoDinamico['paradas']>,
  ): void {
    const bounds = new mapboxgl.LngLatBounds();
    paradas.forEach((parada) => {
      const coordenadas = parada.geojson?.coordinates as
        | [number, number]
        | undefined;
      if (!coordenadas) return;
      this.agregarPunto(coordenadas, parada.direccion ?? parada.nombre);
      bounds.extend(coordenadas);
    });
    if (!bounds.isEmpty()) {
      this.map?.fitBounds(bounds, { padding: 80, duration: 0 });
    }
  }

  // PUNTOS

  private crearElementoMarker(numero: number): HTMLElement {
    const el = document.createElement('div');
    el.className = 'planificador-marker';
    el.textContent = String(numero);
    return el;
  }

  public formatCoordenadas(c: [number, number]): string {
    return `${c[1].toFixed(5)}, ${c[0].toFixed(5)}`;
  }

  /** Número visible del punto (posición actual en la lista, 1-based). */
  public numeroDePunto(punto: PuntoRuta): number {
    return this.puntos.indexOf(punto) + 1;
  }

  private renumerarMarkers(): void {
    this.puntos.forEach((p, i) => {
      p.marker.getElement().textContent = String(i + 1);
    });
  }

  public agregarPunto(coordenadas: [number, number], direccion?: string): void {
    const marker = new mapboxgl.Marker({
      element: this.crearElementoMarker(this.puntos.length + 1),
    })
      .setLngLat(coordenadas)
      .addTo(this.map!);

    // Reasignar el array (no `.push` mutando in-place): los ng-select de
    // inicio/fin están bindeados a `puntos` por referencia, y una mutación
    // sin reasignar no dispara su detección de cambios — el punto ya
    // seleccionado dejaba de mostrarse hasta apagar y prender el toggle.
    this.puntos = [...this.puntos, { coordenadas, direccion, marker }];
    this.invalidarResultado();
    this.actualizarCirculosTolerancia();
  }

  public eliminarPunto(punto: PuntoRuta): void {
    punto.marker.remove();
    this.puntos = this.puntos.filter((p) => p !== punto);
    this.renumerarMarkers();

    if (this.puntoInicio === punto) this.puntoInicio = undefined;
    if (this.puntoFin === punto) this.puntoFin = undefined;

    this.invalidarResultado();
    this.actualizarCirculosTolerancia();
  }

  public limpiarTodo(): void {
    this.puntos.forEach((p) => p.marker.remove());
    this.puntos = [];
    this.puntoInicio = undefined;
    this.puntoFin = undefined;
    this.invalidarResultado();
    this.actualizarCirculosTolerancia();
  }

  /**
   * Dibuja (o redibuja) el círculo de radio real alrededor de cada punto,
   * para ver de un vistazo cuánta tolerancia le estás dando a cada uno —
   * se llama al agregar/quitar puntos y cada vez que cambia el radio.
   */
  public actualizarCirculosTolerancia(): void {
    if (!this.map) return;

    const data: GeoJSON.FeatureCollection = {
      type: 'FeatureCollection',
      features: this.puntos.map(
        (p) =>
          turf.circle(p.coordenadas, this.radioToleranciaMetros, {
            steps: 64,
            units: 'meters',
          }) as GeoJSON.Feature,
      ),
    };

    const source = this.map.getSource(
      TOLERANCIA_SOURCE_ID,
    ) as mapboxgl.GeoJSONSource;
    if (source) {
      source.setData(data);
      return;
    }

    this.map.addSource(TOLERANCIA_SOURCE_ID, { type: 'geojson', data });
    this.map.addLayer({
      id: `${TOLERANCIA_SOURCE_ID}-fill`,
      type: 'fill',
      source: TOLERANCIA_SOURCE_ID,
      paint: { 'fill-color': '#1976d2', 'fill-opacity': 0.15 },
    });
    this.map.addLayer({
      id: `${TOLERANCIA_SOURCE_ID}-line`,
      type: 'line',
      source: TOLERANCIA_SOURCE_ID,
      paint: { 'line-color': '#1976d2', 'line-width': 2 },
    });
  }

  private invalidarResultado(): void {
    this.resultado = undefined;
    const source = this.map?.getSource(
      RUTA_SOURCE_ID,
    ) as mapboxgl.GeoJSONSource;
    source?.setData({
      type: 'Feature',
      properties: {},
      geometry: { type: 'LineString', coordinates: [] },
    });
  }

  // BÚSQUEDA DE DIRECCIONES

  private listenDirChange(): void {
    fromEvent<Event>(this.inputDireccion?.nativeElement, 'keyup')
      .pipe(
        map((e) => (e.target as HTMLInputElement).value),
        filter((value) => !this.buscandoDireccion && value.length > 2),
        debounceTime(700),
        distinctUntilChanged(),
      )
      .subscribe(() => this.buscarDireccion());
  }

  public async buscarDireccion(): Promise<void> {
    if (!this.direccionBuscada || this.direccionBuscada.length < 3) return;
    this.buscandoDireccion = true;
    try {
      const center = this.map?.getCenter();
      const coordenadas: [number, number] | undefined = center
        ? [center.lng, center.lat]
        : undefined;
      const res = await this.direccionesService.autocomplete(
        this.direccionBuscada,
        coordenadas,
      );
      this.predicciones = res.resultados;
      this.showSuggestions = !!this.predicciones?.length;
    } catch (error) {
      this.helper.notifError(error);
    }
    this.buscandoDireccion = false;
  }

  public async seleccionarPrediccion(pred: string): Promise<void> {
    this.showSuggestions = false;
    this.predicciones = undefined;
    try {
      const coordenadas = await this.direccionesService.geocode(pred);
      this.agregarPunto(coordenadas, pred);
      this.map?.flyTo({ center: coordenadas, zoom: 15, duration: 800 });
    } catch (error) {
      this.helper.notifError(error);
    }
    this.direccionBuscada = '';
  }

  public onInputBlur(): void {
    // Delay para permitir que el click en las sugerencias funcione
    setTimeout(() => (this.showSuggestions = false), 200);
  }

  public onInputFocus(): void {
    if (this.predicciones?.length) this.showSuggestions = true;
  }

  // CÁLCULO DE LA MEJOR RUTA

  public async calcularMejorRuta(): Promise<void> {
    if (!this.puedeCalcular) return;

    this.calculando = true;
    try {
      let puntosEnviados: PuntoRuta[];
      let opciones: {
        roundtrip: boolean;
        source: 'any' | 'first';
        destination: 'any' | 'last';
      };

      if (this.definirInicioFin && this.puntoInicio && this.puntoFin) {
        const resto = this.puntos.filter(
          (p) => p !== this.puntoInicio && p !== this.puntoFin,
        );
        puntosEnviados = [this.puntoInicio, ...resto, this.puntoFin];
        opciones = { roundtrip: false, source: 'first', destination: 'last' };
      } else {
        puntosEnviados = this.puntos;
        opciones = { roundtrip: true, source: 'any', destination: 'any' };
      }

      const coords = puntosEnviados.map((p) => p.coordenadas);
      const res = await this.recorridosService.calcularMejorRuta(
        coords,
        opciones,
      );

      // ordenOriginalIndices son índices sobre `puntosEnviados`; se traduce
      // al orden final de `this.puntos` para renumerar markers y lista.
      this.puntos = res.ordenOriginalIndices.map((i) => puntosEnviados[i]);
      this.renumerarMarkers();

      const source = this.map?.getSource(
        RUTA_SOURCE_ID,
      ) as mapboxgl.GeoJSONSource;
      const geometry: GeoJSON.Feature = {
        type: 'Feature',
        properties: {},
        geometry: { type: 'LineString', coordinates: res.geometry },
      };
      if (source) {
        source.setData(geometry);
      } else if (this.map) {
        this.map.addSource(RUTA_SOURCE_ID, { type: 'geojson', data: geometry });
        this.map.addLayer({
          id: RUTA_SOURCE_ID,
          type: 'line',
          source: RUTA_SOURCE_ID,
          layout: { 'line-join': 'round', 'line-cap': 'round' },
          paint: { 'line-color': '#1976d2', 'line-width': 5 },
        });
      }

      this.resultado = {
        distanciaKm: res.distance / 1000,
        duracionMin: res.duration / 60,
        geometry: res.geometry,
      };
      this.helper.notifSuccess('Mejor ruta calculada');
    } catch (error) {
      this.helper.notifError(error);
    }
    this.calculando = false;
  }
}
